import { computed, unref } from 'vue';
import { useTraits } from './useTraits';
import { useColor } from '../modules/color/composables/useColor';
import { useSize } from '../modules/size/composables/useSize';
import { useUpc } from './useUpc';
import { useType } from '../modules/type/composables/useType';
import { getComplexUpcKey, isDefinedTrait } from '../utils/getComplexUpcKey';

/**
  * @param {import('@vueuse/core').MaybeRef<ProductId>} productId
  */
export const useAvailability = (productId) => {
  const { upcData } = useUpc(productId);
  const { colors } = useColor(productId);
  const { sizes } = useSize(productId);
  const { types } = useType(productId);

  const {
    selectedSize, selectedColorId, selectedColor, selectedTypeId,
  } = useTraits(productId);
  const upcMap = computed(() => upcData.value.upcMap);
  const upcs = computed(() => upcData.value.upcs);

  /**
   * @param {number|string} [colorId]
   * @param {number|string} [sizeOrTypeId]
   * @param {number|string} [typeId]
   */
  const getUpcAvailable = (colorId, sizeOrTypeId, typeId) => {
    const complexKey = getComplexUpcKey(colorId, sizeOrTypeId, typeId);

    const upcId = upcMap.value[complexKey];
    const upc = upcs.value[upcId];

    if (!upc) {
      return false;
    }
    const { availability } = upc;

    return !!(availability.available || availability?.bopsAvailable);
  };

  /**
   * Handles an edgecase for bcom when orientation exists and only a single size "No Size" is defined, but is missing from upcMap
   * @param {number|string} [colorId]
   * @param {number|string} [typeId]
   * @returns {boolean}
   */
  function getUpcAvailableWhenTypeWithNoSize(colorId, typeId) {
    return isDefinedTrait(typeId) ? getUpcAvailable(colorId, typeId) : false;
  }

  /**
    * @param {Color} color
    * @param {number} [sizeId]
    * @param {number} [typeId]
    */
  const isColorAvailable = (color, sizeId, typeId) => {
    const hasValidSize = sizeId !== undefined && sizeId !== null;
    const hasValidType = typeId !== undefined && typeId !== null;
    const hasUnselectedSizes = sizes.value.length && !hasValidSize;
    const hasUnselectedTypes = types.value.length && !hasValidType;

    if (hasUnselectedSizes && hasValidType && color.typeToSizes) {
      return !!color.typeToSizes?.[typeId];
    }

    if (hasUnselectedTypes && hasValidSize && color.sizeToTypes) {
      return !!color.sizeToTypes?.[sizeId];
    }

    if (!hasValidSize && !hasValidType) {
      return true;
    }

    const isAvailable = getUpcAvailable(
      color.id,
      sizeId,
      typeId,
    ) || getUpcAvailableWhenTypeWithNoSize(color.id, typeId);

    return isAvailable;
  };

  /**
   * @param {Size} size
   * @param {number | null} colorId
   * @param {number} [typeId]
   */
  const isSizeAvailable = (size, colorId, typeId) => {
    const hasValidSize = size.id !== undefined && size.id !== null;
    const hasValidType = typeId !== undefined && typeId !== null;
    const hasUnselectedSizes = sizes.value.length && !hasValidSize;
    const hasUnselectedTypes = types.value.length && !hasValidType;
    const selectedColorObj = unref(selectedColor);

    if (colorId && hasUnselectedTypes && hasValidSize && selectedColorObj.sizeToTypes) {
      return !!selectedColorObj.sizeToTypes?.[size.id];
    }
    if (colorId && hasUnselectedSizes && hasValidType && selectedColorObj.typeToSizes) {
      return !!selectedColorObj.typeToSizes?.[typeId];
    }

    if (!colorId || (types.value.length && !hasValidType)) {
      return true;
    }

    const isAvailable = getUpcAvailable(
      colorId,
      size.id,
      typeId,
    ) || getUpcAvailableWhenTypeWithNoSize(colorId, typeId);

    return isAvailable;
  };

  const color = computed(() => {
    /** @type {Record<string, boolean>} */
    const data = {};

    const sizeId = unref(selectedSize)?.id ?? undefined;
    const typeId = unref(selectedTypeId) ?? undefined;

    return unref(colors).reduce((result, curr) => {
      result[curr.id] = isColorAvailable(curr, sizeId, typeId);
      return result;
    }, data);
  });

  const size = computed(() => {
    /** @type {Record<string, boolean>} */
    const data = {};

    const colorId = unref(selectedColorId);
    const typeId = unref(selectedTypeId) ?? undefined;

    return unref(sizes).reduce((result, curr) => {
      result[curr.id] = isSizeAvailable(curr, colorId, typeId);
      return result;
    }, data);
  });

  const type = computed(() => {
    /** @type {Record<string, boolean>} */
    const data = {};

    const colorId = unref(selectedColorId);
    const sizeObj = unref(selectedSize) ?? {};
    const selectedColorObj = unref(selectedColor);

    // Checking availability only if we have sizeToTypes and typeToSizes, which will be true only for 3rd variant
    if (selectedColorObj?.sizeToTypes && selectedColorObj?.typeToSizes) {
      return unref(types).reduce((result, curr) => {
        result[curr.id] = isSizeAvailable(sizeObj, colorId, curr.id);
        return result;
      }, data);
    }

    return unref(types).reduce((result, curr) => {
      result[curr.id] = true;
      return result;
    }, data);
  });

  return {
    color,
    size,
    type,
    isColorAvailable,
    isSizeAvailable,
  };
};

export default useAvailability;
